import { useEffect, useState } from "react";
import { Link, Route, Routes, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogOut, User, Car, Heart, PackageOpen, Trash2, Plus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

function AccountShell({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-max rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            {user?.picture ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>}
            <div className="min-w-0">
              <div className="text-sm font-heading font-semibold text-slate-900 truncate">{user?.name || "My Account"}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <nav className="mt-3 space-y-1">
            {[
              { to: "/account", label: "Overview", icon: User, end: true },
              { to: "/account/garage", label: "My Garage", icon: Car },
              { to: "/account/wishlist", label: "Wishlist", icon: Heart },
              { to: "/account/orders", label: "Orders", icon: PackageOpen },
            ].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`account-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#0055FF]/10 text-[#0055FF]" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          {user && (
            <Button variant="outline" onClick={logout} data-testid="account-logout" className="w-full mt-4 gap-1.5"><LogOut className="w-4 h-4" /> Sign out</Button>
          )}
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}

function LoginGate() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startLogin = () => {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <AccountShell>
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
        <User className="w-14 h-14 text-[#0055FF] mx-auto" />
        <h2 className="mt-4 font-heading text-2xl font-bold text-slate-900">Sign in to your account</h2>
        <p className="mt-2 text-slate-500 max-w-md mx-auto">Save vehicles to your garage, keep a wishlist, and view your order history.</p>
        <Button data-testid="account-google-signin" onClick={startLogin} className="mt-6 h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC]">Continue with Google</Button>
      </div>
    </AccountShell>
  );
}

function Overview() {
  const { user } = useAuth();
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Welcome</div>
        <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{user?.name}</div>
        <p className="text-sm text-slate-500 mt-1">Manage your saved vehicles, wishlist, and order history.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Link to="/account/garage" className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0055FF] transition-colors">
          <Car className="w-5 h-5 text-[#0055FF]" />
          <div className="mt-3 font-heading font-semibold text-slate-900">My Garage</div>
          <div className="text-xs text-slate-500 mt-1">Save vehicles for faster fitment checks.</div>
        </Link>
        <Link to="/account/wishlist" className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0055FF] transition-colors">
          <Heart className="w-5 h-5 text-[#0055FF]" />
          <div className="mt-3 font-heading font-semibold text-slate-900">Wishlist</div>
          <div className="text-xs text-slate-500 mt-1">Save products to consider later.</div>
        </Link>
        <Link to="/account/orders" className="rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0055FF] transition-colors">
          <PackageOpen className="w-5 h-5 text-[#0055FF]" />
          <div className="mt-3 font-heading font-semibold text-slate-900">Orders</div>
          <div className="text-xs text-slate-500 mt-1">View past orders and tracking.</div>
        </Link>
      </div>
    </div>
  );
}

function Garage() {
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/account/garage").then(({ data }) => setVehicles(data.vehicles || [])).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target));
    try {
      await api.post("/account/garage", fd);
      toast.success("Vehicle saved.");
      setShowForm(false); refresh();
    } catch { toast.error("Could not save."); }
  };
  const del = async (id) => { await api.delete(`/account/garage/${id}`); refresh(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Garage</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="garage-add-toggle"><Plus className="w-4 h-4 mr-1" /> Add vehicle</Button>
      </div>
      {showForm && (
        <form onSubmit={add} className="rounded-xl border border-slate-200 bg-white p-5 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="garage-form">
          <div><Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Nickname</Label><Input name="nickname" className="mt-1" data-testid="garage-nickname" /></div>
          <div><Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Year *</Label><Input name="year" required className="mt-1" data-testid="garage-year" /></div>
          <div><Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Make *</Label><Input name="make" required className="mt-1" data-testid="garage-make" /></div>
          <div><Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Model *</Label><Input name="model" required className="mt-1" data-testid="garage-model" /></div>
          <div><Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Engine</Label><Input name="engine" className="mt-1" data-testid="garage-engine" /></div>
          <div><Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">VIN</Label><Input name="vin" className="mt-1" data-testid="garage-vin" /></div>
          <div className="col-span-full"><Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="garage-submit">Save vehicle</Button></div>
        </form>
      )}
      {loading ? (
        <Skeleton className="h-24 rounded-xl" />
      ) : vehicles.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">No vehicles saved yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {vehicles.map((v) => (
            <div key={v.vehicle_id} className="rounded-xl border border-slate-200 bg-white p-5 flex items-start justify-between">
              <div>
                <div className="font-heading font-semibold text-slate-900">{v.nickname || `${v.year} ${v.make} ${v.model}`}</div>
                <div className="text-xs font-mono text-slate-500 mt-1">{v.year} · {v.make} · {v.model}{v.engine ? ` · ${v.engine}` : ""}</div>
                {v.vin && <div className="text-xs font-mono text-slate-400 mt-1">VIN {v.vin}</div>}
              </div>
              <button onClick={() => del(v.vehicle_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Wishlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/account/wishlist").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false)); }, []);
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">Wishlist</h1>
      {loading ? <Skeleton className="h-24 rounded-xl" /> : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">Your wishlist is empty.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((p) => (
            <Link to={`/product/${p.slug}`} key={p.product_id} className="rounded-xl border border-slate-200 bg-white p-3 flex gap-3">
              <div className="w-20 h-20 bg-slate-50 rounded-md overflow-hidden shrink-0"><img src={p.main_image} alt="" className="w-full h-full object-contain" /></div>
              <div className="min-w-0">
                <div className="text-sm font-heading font-semibold text-slate-900 line-clamp-2">{p.title}</div>
                <div className="text-slate-900 font-heading font-bold mt-1">{formatPrice(p.price)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/account/orders").then(({ data }) => setOrders(data.orders || [])).finally(() => setLoading(false)); }, []);
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">Orders</h1>
      {loading ? <Skeleton className="h-24 rounded-xl" /> : orders.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.order_id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-semibold text-slate-900">Order {o.order_id}</div>
                  <div className="text-xs font-mono text-slate-500">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-bold text-slate-900">{formatPrice(o.total)}</div>
                  <div className={`text-xs font-mono uppercase tracking-widest ${o.payment_status === "paid" ? "text-emerald-600" : "text-slate-500"}`}>{o.payment_status}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500">{o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? "s" : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const { user, loading } = useAuth();
  if (loading) return <AccountShell><Skeleton className="h-40 rounded-xl" /></AccountShell>;
  if (!user) return <LoginGate />;
  return (
    <AccountShell>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="garage" element={<Garage />} />
        <Route path="wishlist" element={<Wishlist />} />
        <Route path="orders" element={<Orders />} />
      </Routes>
    </AccountShell>
  );
}
